/**
 * Horizontal gridline values for a panel, at round numbers.
 *
 * The charts drew only their top and bottom, so reading a magnitude off one
 * meant interpolating by eye across the whole panel — 17 kt somewhere between a
 * 0 at the bottom and a 30 at the top, on 84 pixels. The shape was legible and
 * the value was not.
 *
 * Steps come from 1/2/5 × 10ⁿ so labels stay clean; 2.5 is deliberately absent
 * because it forces a decimal place onto axes that otherwise read as integers.
 * The midpoint fallback below may land off that ladder — a 0-8 ft axis gets a
 * step of 4 — which is fine, because the requirement is a clean *label*, and
 * "4" is one.
 *
 * The smallest step that fits the budget wins, rather than the largest that
 * does. Given a symmetric current axis of ±2.5 kt and room for three lines, a
 * step of 5 yields one line and a step of 2 yields three — the denser reading is
 * the useful one, so the search runs upward and stops at the first fit.
 */

const NICE_STEPS = [1, 2, 5];

/**
 * How many gridlines a panel of this height can carry.
 *
 * The floor of three is what makes the short panels worth doing at all: 46px of
 * water level with only its bounds drawn is the case that prompted this, and two
 * lines is not a grid.
 */
export function gridLineBudget(heightPx: number, minSpacingPx = 24): number {
  return Math.max(3, Math.floor(heightPx / minSpacingPx) + 1);
}

function candidateSteps(span: number): number[] {
  const out: number[] = [];
  // Well below any plausible step for this data, up to well above it.
  const from = Math.floor(Math.log10(span)) - 3;
  for (let p = from; p <= from + 6; p++) {
    for (const s of NICE_STEPS) out.push(s * 10 ** p);
  }
  return out.sort((a, b) => a - b);
}

/**
 * Snap a computed tick to a clean number.
 *
 * Two hazards, both of which reach the screen. Repeated addition drifts —
 * stepping 0.2 from zero reaches 0.6000000000000001 — and `Math.ceil` of a tiny
 * negative returns `-0`, which renders as "-0" on the axis. The same negative
 * zero already had to be normalised once in this project, in the tide readout.
 */
function clean(v: number): number {
  const r = Number(v.toFixed(6));
  return r === 0 ? 0 : r;
}

/** Values strictly between the bounds, at `step`. */
function interiorTicks(lo: number, hi: number, step: number): number[] {
  const out: number[] = [];
  // Clearance is a fraction of the panel, not of the step: the thing being
  // avoided is two labels colliding in pixels. Keyed to the step instead, a
  // 53-57 °F axis rejected 55 — exactly the midpoint it most needed.
  const clearance = (hi - lo) * 0.18;
  const first = Math.ceil(lo / step - 1e-9) * step;
  for (let v = first; v < hi; v += step) {
    const t = clean(v);
    if (t > lo + clearance && t < hi - clearance) out.push(t);
  }
  return out;
}

/**
 * The roundest value near the middle of the range.
 *
 * Some spans have no step on 1/2/5 that yields exactly one interior line: −1 to
 * 7 ft offers two lines or six, and a 46px panel has room for neither. Rather
 * than fall back to bare bounds — the thing this module exists to fix — place a
 * single line at whatever round number sits nearest the middle.
 */
function midpointTick(lo: number, hi: number): number[] {
  const mid = (lo + hi) / 2;
  const clearance = (hi - lo) * 0.18;

  for (const step of candidateSteps(hi - lo)) {
    const t = clean(Math.round(mid / step) * step);
    if (t > lo + clearance && t < hi - clearance) return [t];
  }
  return [];
}

/** Ratio of the largest gap to the smallest. 1 is perfectly even. */
function unevenness(ticks: number[]): number {
  const gaps: number[] = [];
  for (let i = 1; i < ticks.length; i++) gaps.push(ticks[i] - ticks[i - 1]);
  if (gaps.length === 0) return 1;
  return Math.max(...gaps) / Math.max(Math.min(...gaps), 1e-9);
}

/**
 * The bounds are always drawn — they are the panel's own edges, and the data's
 * extremes — so the budget covers them and the interior lines together.
 *
 * Every candidate is scored rather than taking the first that fits. Scanning
 * upward and stopping at the first put a single line at 5 on a −1..7 ft axis,
 * six units above the floor and two below the ceiling; the midpoint at 3 is the
 * obviously better grid. So: the densest set that fits the budget wins, and
 * among equally dense sets, the most evenly spaced.
 */
export function gridTicks(lo: number, hi: number, maxLines: number): number[] {
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return [clean(lo)];
  if (maxLines < 3) return [clean(lo), clean(hi)];

  const options = candidateSteps(hi - lo)
    .map((step) => interiorTicks(lo, hi, step))
    .concat([midpointTick(lo, hi)])
    .filter((interior) => interior.length > 0 && interior.length + 2 <= maxLines)
    .map((interior) => [clean(lo), ...interior, clean(hi)]);

  if (options.length === 0) return [clean(lo), clean(hi)];

  return options.reduce((best, next) => {
    if (next.length !== best.length) return next.length > best.length ? next : best;
    return unevenness(next) < unevenness(best) ? next : best;
  });
}

/**
 * Decimal places the labels need, taken from the tick values themselves.
 *
 * Not from the range, which puts a spurious `.0` on every label whenever the
 * span happens to be small. And not from the step either: a 57–60 °F axis takes
 * a step of 1.5, whose integer-looking size hid that the middle line sits at
 * 58.5. Rounded to "59" it made an evenly spaced grid read as 57/59/60 — the
 * exact uneven look this module exists to remove.
 *
 * So: the fewest decimals that render every tick truthfully, capped at two.
 */
export function tickDecimals(ticks: readonly number[]): number {
  for (let d = 0; d <= 2; d++) {
    if (ticks.every((v) => Math.abs(v - Number(v.toFixed(d))) < 1e-9)) return d;
  }
  return 2;
}
